/**
 * Единственная точка отправки событий (TRK-2). Немедленные — одиночным POST,
 * остальные — батчем каждые 2 с или по 20 событий; на pagehide — sendBeacon.
 * Все вызовы fire-and-forget: ошибка трекинга UX не ломает.
 */
import { api } from '@/api/client';
import { buildEnvelope, type TrackData } from './envelope';
import { isImmediateEvent, isPaymentEvent, type AppEventType } from './events';

const SINGLE_ENDPOINT = '/landing-event';
const BATCH_ENDPOINT = '/landing-events/batch';
/** Совпадает с `max_batch` конфига приёма. */
const MAX_BATCH_SIZE = 50;
const QUEUE_FLUSH_SIZE = 20;
const QUEUE_FLUSH_MS = 2000;

let queue: Record<string, unknown>[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let unloadHooksInstalled = false;
let batchUnsupported = false;
/** По умолчанию включено (неопределённость = платный режим); PaywallProvider выключает в бесплатном. */
let paymentEventsEnabled = true;

export function setPaymentEventsEnabled(enabled: boolean): void {
  paymentEventsEnabled = enabled;
}

export function track(type: AppEventType, data: TrackData = {}): void {
  if (typeof window === 'undefined') return;
  if (!paymentEventsEnabled && isPaymentEvent(type)) return;
  const payload = buildEnvelope(type, data) as unknown as Record<string, unknown>;

  if (isImmediateEvent(type)) {
    api.post(SINGLE_ENDPOINT, payload).catch(() => {});
    return;
  }
  enqueue(payload);
}

/** С Bearer и fetch keepalive — для событий на уходе со страницы, где важен user_id. */
export function trackKeepalive(type: AppEventType, data: TrackData = {}): void {
  if (typeof window === 'undefined') return;
  api.postKeepalive(
    SINGLE_ENDPOINT,
    buildEnvelope(type, data) as unknown as Record<string, unknown>,
  );
}

function enqueue(payload: Record<string, unknown>): void {
  installUnloadHooks();
  queue.push(payload);

  if (queue.length >= QUEUE_FLUSH_SIZE) {
    flushQueue();
    return;
  }
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushQueue();
    }, QUEUE_FLUSH_MS);
  }
}

export function flushQueue(viaBeacon = false): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const pending = queue;
  queue = [];
  for (let i = 0; i < pending.length; i += MAX_BATCH_SIZE) {
    sendBatch(pending.slice(i, i + MAX_BATCH_SIZE), viaBeacon);
  }
}

function sendBatch(events: Record<string, unknown>[], viaBeacon: boolean): void {
  if (viaBeacon) {
    if (batchUnsupported) {
      for (const event of events) api.beacon(SINGLE_ENDPOINT, event);
      return;
    }
    api.beacon(BATCH_ENDPOINT, { events });
    return;
  }

  if (batchUnsupported) {
    for (const event of events) api.post(SINGLE_ENDPOINT, event).catch(() => {});
    return;
  }

  api.post(BATCH_ENDPOINT, { events }).catch((error: unknown) => {
    // 404/405 — роута нет: дальше не пробуем; прочее (сеть, 429) считаем временным.
    const status = (error as { status?: number } | null)?.status;
    if (status === 404 || status === 405) batchUnsupported = true;
    for (const event of events) api.post(SINGLE_ENDPOINT, event).catch(() => {});
  });
}

function installUnloadHooks(): void {
  if (unloadHooksInstalled || typeof window === 'undefined') return;
  unloadHooksInstalled = true;
  // pagehide покрывает bfcache и закрытие вкладки в iOS Safari.
  window.addEventListener('pagehide', () => flushQueue(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushQueue(true);
  });
}
